import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import OtpVerify from "../components/OtpVerify";
import { PhoneField, EmailField, AddressCheck, ConsentYesNo } from "../components/ContactFields";
import { emailCheck, phoneValid } from "../lib/contact";
import {
  User, Phone, Cake, Mail, CheckCircle2, MapPin, Home, Hash, Lock, Store,
  CreditCard, Printer,
} from "lucide-react";

export default function IssueCard() {
  const { user } = useAuth();
  const { settings } = useAppSettings();
  const [otpId, setOtpId] = useState(null);
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", dob: "",
    password: "", password_confirm: "",
    address: "", city: "", zip_code: "", province: "",
    branch_id: "",
    consent: false,
    consent_marketing_email: null,
    consent_marketing_wa: null,
    consent_profiling: null,
    card_type: "virtual", // "physical" richiede codice preesistente, "virtual" auto-assegnato
    card_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const consentsAnswered = [form.consent_marketing_email, form.consent_marketing_wa, form.consent_profiling].every((v) => v === true || v === false);
  const fullName = `${form.first_name} ${form.last_name}`.trim();
  const [codeCheck, setCodeCheck] = useState(null); // {available, exists, status, type}
  const [duplicates, setDuplicates] = useState(null); // {candidates:[...]}

  useEffect(() => {
    // Admin picks the branch. Staff always uses their assigned branch.
    if (isAdmin) {
      api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
    }
  }, [isAdmin]);

  const submit = async (e, forceConfirm = false, skipOtp = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.consent) return toast.error("Devi accettare il trattamento dati");
    if (!consentsAnswered) return toast.error("Rispondi SI o NO a tutti i consensi privacy");
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Inserisci nome e cognome");
    if (!phoneValid(form.phone)) return toast.error("Numero di telefono non valido");
    if (!emailCheck(form.email).valid) return toast.error("Email non valida: correggila prima di procedere");
    if (!skipOtp && !otpId) return toast.error("Verifica prima il cliente con il codice OTP");
    if (!form.password || form.password.length < 6) return toast.error("Password minima 6 caratteri");
    if (form.password !== form.password_confirm) return toast.error("Le password non coincidono");
    if (isAdmin && !form.branch_id) return toast.error("Seleziona la sede che rilascia la carta");
    if (form.card_type === "physical") {
      if (!form.card_code || form.card_code.trim().length < 3) return toast.error("Inserisci il codice della carta fisica");
      if (codeCheck && !codeCheck.available) return toast.error("Codice non disponibile");
    }
    setLoading(true);
    try {
      const { password_confirm: _pc, ...payload } = form;
      payload.name = fullName;
      if (!isAdmin) delete payload.branch_id;
      payload.card_code = form.card_type === "physical" ? (form.card_code || "").trim().toUpperCase() : undefined;
      payload.force_confirm_duplicate = !!forceConfirm;
      payload.otp_id = otpId || null;
      payload.skip_otp = !!skipOtp && !otpId;
      const { data } = await api.post("/customers/issue-card", payload);
      setSuccess(data);
      setOtpId(null);
      setDuplicates(null);
      toast.success("Carta rilasciata! 🎉");
    } catch (err) {
      const det = err?.response?.data?.detail;
      // Il backend può restituire un dict con code=duplicate_name + candidates
      if (typeof det === "object" && det?.code === "duplicate_name") {
        setDuplicates(det);
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setLoading(false); }
  };

  // Verifica in tempo reale il codice della carta fisica
  const verifyCode = async (code) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) { setCodeCheck(null); return; }
    try {
      const { data } = await api.get(`/card-codes/check/${encodeURIComponent(c)}`);
      setCodeCheck(data);
    } catch (err) { setCodeCheck(null); }
  };

  const reset = () => {
    setSuccess(null);
    setForm({
      first_name: "", last_name: "", phone: "", email: "", dob: "",
      password: "", password_confirm: "",
      address: "", city: "", zip_code: "", province: "",
      branch_id: "", consent: false,
      consent_marketing_email: null,
      consent_marketing_wa: null,
      consent_profiling: null,
      card_type: "virtual", card_code: "",
    });
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="issue-card-page">
      <Toaster theme="light" position="top-right" richColors />
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <CreditCard className="h-3 w-3 text-amber-300" /> Punto vendita
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-1">Rilascio Card</h1>
        <p className="text-zinc-400 mt-1 text-sm">
          Compila il modulo per rilasciare una nuova carta fedeltà al cliente.
          La carta resta legata a questa sede ma sarà utilizzabile su tutta la rete.
        </p>
      </div>

      {success ? (
        <div className="card-elev p-8 text-center animate-fade-up" data-testid="issue-card-success">
          <CheckCircle2 className="h-14 w-14 text-emerald-300 mx-auto mb-3" />
          <h2 className="font-heading text-3xl font-bold mb-1">Carta attivata</h2>
          <p className="text-zinc-400 mb-4">
            Consegna il numero carta al cliente o invitalo ad accedere all'area riservata.
          </p>
          {success.welcome_coupon && (
            <div className="mx-auto max-w-sm mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-left" data-testid="issue-card-welcome-coupon">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">🎁 Coupon benvenuto</div>
              <div className="font-heading font-bold text-lg text-amber-100">{success.welcome_coupon.name}</div>
              <div className="font-mono text-sm text-amber-200 mt-1">
                Codice: <span className="text-zinc-100 font-bold">{success.welcome_coupon.code}</span> · Sconto: {success.welcome_coupon.discount_type === "percent" ? `${success.welcome_coupon.discount_value}%` : `€ ${success.welcome_coupon.discount_value}`}
              </div>
            </div>
          )}
          <div className="inline-flex flex-col items-center gap-3 p-6 rounded-xl bg-zinc-950 border border-zinc-800">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=6&bgcolor=ffffff&color=1e3a8a&data=${encodeURIComponent(success.card_number)}`}
              alt="QR" className="rounded-lg"
            />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Numero carta</div>
            <div className="font-mono text-2xl font-bold text-amber-300" data-testid="issue-card-number">{success.card_number}</div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <Button
              data-testid="issue-card-open-portal"
              onClick={() => nav(`/carta/${success.card_number}`)}
              className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              Apri portale cliente
            </Button>
            <Button
              data-testid="issue-card-print"
              variant="outline"
              onClick={() => window.print()}
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            >
              <Printer className="h-4 w-4 mr-1" /> Stampa
            </Button>
            <Button
              data-testid="issue-card-another"
              variant="outline"
              onClick={reset}
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            >
              Rilascia un'altra
            </Button>
            {success.privacy_pdf && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${success.privacy_pdf}`}
                target="_blank"
                rel="noreferrer"
                className="pill pill-gold text-sm hover:opacity-90 self-center"
                data-testid="issue-card-privacy-pdf"
              >
                Scarica consenso PDF
              </a>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="card-elev p-6 sm:p-8 space-y-5 animate-fade-up" data-testid="issue-card-form">
          {/* TIPO DI CARTA — deve essere la prima scelta operatore */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-2">
              <CreditCard className="h-3 w-3" /> Tipo di carta*
            </div>
            <div className="grid grid-cols-2 gap-2" data-testid="issue-card-type">
              {["virtual", "physical"].map((t) => {
                const on = form.card_type === t;
                return (
                  <button
                    type="button" key={t}
                    data-testid={`issue-card-type-${t}`}
                    onClick={() => { setForm({ ...form, card_type: t, card_code: t === "virtual" ? "" : form.card_code }); setCodeCheck(null); }}
                    className={`p-3 rounded-lg border text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10 text-amber-100" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"}`}
                  >
                    <div className="text-sm font-semibold">{t === "virtual" ? "Virtuale (auto)" : "Fisica (codice)"}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {t === "virtual" ? "Il sistema assegna automaticamente un codice dal pool virtuale" : "Inserisci il codice della carta fisica pre-caricata"}
                    </div>
                  </button>
                );
              })}
            </div>
            {form.card_type === "physical" && (
              <div className="mt-3">
                <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-2">
                  <Hash className="h-3.5 w-3.5 text-zinc-500" /> Codice carta fisica*
                </Label>
                <Input
                  data-testid="issue-card-code"
                  value={form.card_code}
                  onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, card_code: v }); verifyCode(v); }}
                  className="bg-zinc-950 border-zinc-800 h-11 font-mono uppercase"
                  placeholder="ES. VIVI0001"
                />
                {codeCheck && (
                  <div className="mt-1 text-[11px]" data-testid="issue-card-code-status">
                    {codeCheck.available && <span className="text-emerald-300">✓ Codice disponibile ({codeCheck.type === "physical" ? "fisica" : "virtuale"})</span>}
                    {codeCheck.exists && !codeCheck.available && <span className="text-red-300">✗ Codice non disponibile (stato: {codeCheck.status})</span>}
                    {!codeCheck.exists && <span className="text-amber-300">⚠ Codice non trovato nel pool — chiedi all'admin di caricarlo</span>}
                  </div>
                )}
              </div>
            )}
          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field icon={User} label="Nome*">
              <Input data-testid="issue-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" placeholder="Mario" />
            </Field>
            <Field icon={User} label="Cognome*">
              <Input data-testid="issue-last-name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" placeholder="Rossi" />
            </Field>
          </div>
          <Field icon={Phone} label="Telefono* (prefisso internazionale + numero)">
            <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testPrefix="issue-phone" className="bg-zinc-950 border-zinc-800 h-11" selectClassName="bg-zinc-950 border-zinc-800 text-zinc-200" />
          </Field>
          <Field icon={Mail} label="Email">
            <EmailField value={form.email} onChange={(v) => setForm({ ...form, email: v })} testPrefix="issue-email" className="bg-zinc-950 border-zinc-800 h-11" />
          </Field>
          <Field icon={Cake} label="Data di nascita (per il regalo compleanno)">
            <Input data-testid="issue-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" />
          </Field>

          <div className="border-t border-zinc-800 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
              <Lock className="h-3 w-3" /> Password per l'area riservata
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field icon={Lock} label="Password* (min. 6 caratteri)">
                <Input data-testid="issue-password" type="password" required minLength={6}
                       value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       className="bg-zinc-950 border-zinc-800 h-11" placeholder="••••••••" />
              </Field>
              <Field icon={Lock} label="Ripeti password*">
                <Input data-testid="issue-password-confirm" type="password" required minLength={6}
                       value={form.password_confirm}
                       onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                       className="bg-zinc-950 border-zinc-800 h-11" placeholder="••••••••" />
              </Field>
            </div>
            {form.password && form.password_confirm && form.password !== form.password_confirm && (
              <div className="text-[11px] text-red-400 mt-1" data-testid="issue-password-mismatch">Le password non coincidono</div>
            )}
          </div>

          {isAdmin ? (
            <div className="border-t border-zinc-800 pt-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
                <Store className="h-3 w-3" /> Sede che rilascia la carta*
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="issue-branches">
                {branches.map((b) => {
                  const on = form.branch_id === b.id;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`issue-branch-${b.id}`}
                      onClick={() => setForm({ ...form, branch_id: b.id })}
                      className={`p-3 rounded-lg border text-left transition-colors ${on ? "border-amber-500 bg-amber-500/10 text-amber-100" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"}`}
                    >
                      <div className="text-sm font-semibold truncate">{b.name}</div>
                      {b.address && <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{b.address}</div>}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-500 mt-2">
                La carta resterà legata a questa sede, ma sarà utilizzabile su tutta la rete.
              </p>
            </div>
          ) : (
            <div className="border-t border-zinc-800 pt-4">
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 flex items-start gap-2 text-xs text-amber-200" data-testid="issue-operator-branch">
                <Store className="h-4 w-4 text-amber-300 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-100">Sede di rilascio</div>
                  <div className="text-amber-200/80">
                    La carta verrà legata alla tua sede operativa. Potrà essere utilizzata su tutta la rete.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-zinc-800 pt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
              <MapPin className="h-3 w-3" /> Indirizzo di consegna (per ordini online)
            </div>
            <Field icon={Home} label="Via e numero civico">
              <Input data-testid="issue-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" placeholder="Via Roma 12" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mt-4">
              <div className="sm:col-span-2">
                <Field icon={Hash} label="CAP">
                  <Input data-testid="issue-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" placeholder="84121" />
                </Field>
              </div>
              <div className="sm:col-span-3">
                <Field icon={MapPin} label="Città">
                  <Input data-testid="issue-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-zinc-950 border-zinc-800 h-11" placeholder="Salerno" />
                </Field>
              </div>
              <div className="sm:col-span-1">
                <Field icon={MapPin} label="Prov.">
                  <Input data-testid="issue-province" maxLength={2} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })} className="bg-zinc-950 border-zinc-800 h-11 uppercase" placeholder="SA" />
                </Field>
              </div>
            </div>
            <AddressCheck form={form} onApply={(s) => setForm({ ...form, ...s })} testPrefix="issue-addr" />
          </div>

          {/* CONSENSI GDPR — al termine della scheda, prima dell'invio */}
          <div className="rounded-xl bg-amber-500/5 border border-amber-500/30 p-4 space-y-3" data-testid="issue-consents-block">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold flex items-center gap-2">
              <Lock className="h-3 w-3" /> Consensi Privacy (GDPR)
              {settings?.privacy_url && (
                <a href={resolveImageUrl(settings.privacy_url)} target="_blank" rel="noreferrer" data-testid="issue-privacy-link"
                  className="ml-auto normal-case tracking-normal text-[11px] font-semibold underline text-sky-300">
                  {settings.privacy_label || "Leggi l'informativa privacy"}
                </a>
              )}
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Il cliente esprime i consensi qui sotto rispondendo SI o NO a ciascuno. Il trattamento base è obbligatorio (solo SI); gli altri sono facoltativi ma la risposta è richiesta e resta revocabile in ogni momento.
            </p>
            <ConsentYesNo testId="issue-consent" required title="Trattamento dati* (obbligatorio)" desc="Autorizzo il trattamento dei miei dati personali per la gestione della carta fedeltà e l'accumulo dei punti, ai sensi del Reg. UE 2016/679." value={form.consent ? true : null} onChange={(v) => setForm({ ...form, consent: !!v })} />
            <ConsentYesNo testId="issue-consent-email" title="Marketing via Email" desc="Promozioni, coupon e volantini via posta elettronica." value={form.consent_marketing_email} onChange={(v) => setForm({ ...form, consent_marketing_email: v })} />
            <ConsentYesNo testId="issue-consent-wa" title="Marketing via WhatsApp / SMS" desc="Comunicazioni promozionali sul telefono." value={form.consent_marketing_wa} onChange={(v) => setForm({ ...form, consent_marketing_wa: v })} />
            <ConsentYesNo testId="issue-consent-profiling" title="Profilazione" desc="Analisi delle abitudini di acquisto per offerte personalizzate." value={form.consent_profiling} onChange={(v) => setForm({ ...form, consent_profiling: v })} />
            {!consentsAnswered && <div className="text-[11px] text-amber-300" data-testid="issue-consents-missing">Rispondi SI o NO a tutti i consensi per poter rilasciare la carta.</div>}
          </div>

          <OtpVerify phone={form.phone} email={form.email} name={fullName} verifiedId={otpId} onVerified={setOtpId} testPrefix="issue-otp" />

          <Button
            data-testid="issue-submit"
            type="submit"
            disabled={loading || !form.consent || !consentsAnswered || !otpId || (form.card_type === "physical" && codeCheck && !codeCheck.available)}
            className="w-full h-11 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] font-semibold"
          >
            {loading ? "Attendere…" : "Rilascia carta fedeltà"}
          </Button>
          {isAdmin && !otpId && (
            <Button
              data-testid="issue-submit-skip-otp"
              type="button"
              variant="outline"
              disabled={loading || !form.consent || !consentsAnswered || (form.card_type === "physical" && codeCheck && !codeCheck.available)}
              onClick={(e) => { if (window.confirm("Emettere la carta senza verifica OTP? L'operazione verrà registrata a tuo nome.")) submit(e, false, true); }}
              className="w-full h-10 border-zinc-800 text-zinc-300"
            >
              Emetti senza verifica (solo admin)
            </Button>
          )}
        </form>
      )}

      {/* Dialog conferma duplicato Cognome+Nome: mostra i clienti simili trovati */}
      {duplicates && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="issue-duplicate-dialog">
          <div className="bg-[color:var(--bg-surface)] border border-amber-500/40 rounded-xl max-w-lg w-full p-5 space-y-3">
            <div className="flex items-center gap-2">
              <span className="pill pill-gold">⚠ POSSIBILE DUPLICATO</span>
            </div>
            <h2 className="font-heading text-xl font-bold">Cliente già registrato?</h2>
            <p className="text-sm text-zinc-400">Ho trovato uno o più clienti con nome/cognome simili. Verifica prima di procedere:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {duplicates.candidates.map((c) => (
                <div key={c.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-sm">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">Carta {c.card_number} · Tel {c.phone}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="issue-duplicate-cancel" variant="outline" onClick={() => setDuplicates(null)} className="border-zinc-700 flex-1">
                Annulla
              </Button>
              <Button data-testid="issue-duplicate-confirm" onClick={() => { setDuplicates(null); submit(null, true); }} className="bg-amber-600 hover:bg-amber-700 flex-1">
                È una persona diversa · Prosegui
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-zinc-500" /> {label}
      </Label>
      {children}
    </div>
  );
}
